// Nosto Category — renders Nosto category merchandising on collection pages.
// Facets rendered by NostoFacets module.
(function () {
  'use strict';

  // Nosto reloads the grid to page 1 top. Stops browser flashing the old deep scroll first.
  try {
    var nav = performance.getEntriesByType('navigation')[0];
    if (nav && (nav.type === 'reload' || nav.type === 'back_forward')) {
      history.scrollRestoration = 'manual';
    }
  } catch (e) { /* ignore */ }

  let facetsRoot;
  let eventsBound = false;
  let searchToken = 0;
  let loadedCount = 0;
  let totalProducts = 0;
  let currentPage = 1;
  const PAGE_SIZE = window.NostoSearch ? window.NostoSearch.defaults.category.pageSize : 36;

  const getPageFromUrl = () => {
    const match = location.search.match(/[?&]page=(\d+)/);
    return match ? Math.max(1, parseInt(match[1], 10)) : 1;
  };

  const setPage = (page) => {
    currentPage = page;
    const url = new URL(location.href);
    if (page > 1) url.searchParams.set('page', page);
    else url.searchParams.delete('page');
    history.replaceState(null, '', url);
  };

  // Build card elements from hits — progressive: cells insert immediately as
  // skeletons and fill in per-card (see NostoSearch.buildCards).
  const buildCards = (hits) => window.NostoSearch.buildCards(hits, 'category');

  // Scroll anchor: land back on the product the user left from on Back. Kept per history entry in
  // history.state so it survives Back. Most Back cases are served by the browser's bfcache with no
  // re-render, so this only really runs on a non-cached Back. (A reload loads page 1 at the top.)
  const productCells = () =>
    document.querySelectorAll('[data-nosto-grid] > .grid-item.product-item[data-handle]');

  const findTopCell = () => {
    let best = null, bestTop = -Infinity;
    productCells().forEach((cell) => {
      const top = cell.getBoundingClientRect().top;
      if (top <= 80 && top > bestTop) { bestTop = top; best = cell; }
    });
    if (!best) productCells().forEach((cell) => { if (!best && cell.getBoundingClientRect().bottom > 0) best = cell; });
    return best;
  };

  const applyAnchor = (anchor) => {
    if (!anchor) return;
    let cell = null;
    productCells().forEach((c) => { if (!cell && c.dataset.handle === anchor.handle) cell = c; });
    if (!cell) return;
    const target = cell.getBoundingClientRect().top + window.scrollY - (anchor.offset || 0);
    window.scrollTo(0, Math.max(0, target));
  };

  const readAnchor = () => {
    const a = history.state && history.state.nostoAnchor;
    if (!a || !a.handle || a.page !== getPageFromUrl()) return null;
    return a;
  };

  const saveAnchor = () => {
    const grid = document.querySelector('[data-nosto-grid]');
    if (!grid || !grid.classList.contains('ns-loaded')) return;
    const cell = findTopCell();
    if (!cell || !cell.dataset.handle) return;
    const anchor = { handle: cell.dataset.handle, offset: Math.round(cell.getBoundingClientRect().top), page: getPageFromUrl() };
    try { history.replaceState(Object.assign({}, history.state, { nostoAnchor: anchor }), ''); } catch (e) { /* ignore */ }
  };

  // Grid snapshot: the same back-restore the native collections already have
  // (custom.js + collection.liquid render the exact grid the visitor left —
  // placeholders above, their products in place — so Back never re-renders
  // under them). A client-rendered grid can't be server-rendered back, so the
  // equivalent is saving the rendered grid itself on leave and restoring it
  // wholesale on Back: same products, same positions, no refetch, no Nosto
  // swap. Cards stay interactive because quick-add etc. are event-delegated.
  // The anchor flow above remains the fallback when no snapshot survives.
  const SNAP_KEY = 'nosto-grid-snapshot';

  // Cards are saved as they were rendered, not as the theme's scripts left them:
  // on desktop a card's image carousel (Flickity) rewrites its slider, and
  // restoring that markup runs Flickity again on top of it, leaving the card
  // white. Cells without a cached card (breakers) are saved as they are.
  const snapshotHtml = (grid) => [...grid.children].map((cell) => {
    const card = cell.dataset.handle ? window.NostoSearch.cardHtml(cell.dataset.handle) : '';
    if (!card) return cell.outerHTML;
    const shell = cell.cloneNode(false).outerHTML;
    const end = shell.lastIndexOf('</');
    return shell.slice(0, end) + card + shell.slice(end);
  }).join('');

  const saveSnapshot = () => {
    const grid = document.querySelector('[data-nosto-grid]');
    if (!grid || !grid.classList.contains('ns-loaded')) return;
    try {
      sessionStorage.setItem(SNAP_KEY, JSON.stringify({
        url: location.pathname + location.search,
        html: snapshotHtml(grid),
        fallback: grid.classList.contains('ns-fallback'),
        scrollY: Math.round(window.scrollY),
        loadedCount: loadedCount,
        currentPage: currentPage,
        totalProducts: totalProducts
      }));
    } catch (e) { /* storage full/unavailable — the anchor restore still applies */ }
  };

  const readSnapshot = () => {
    try {
      const snap = JSON.parse(sessionStorage.getItem(SNAP_KEY));
      return (snap && snap.html && snap.url === location.pathname + location.search) ? snap : null;
    } catch (e) { return null; }
  };

  const restoreSnapshot = (snap) => {
    const grid = document.querySelector('[data-nosto-grid]');
    if (!grid) return false;
    // The page has reloaded, so the card cache is empty: refill it from the saved
    // cards, or the next leave saves them as Flickity left them and the Back after
    // that turns them white. Cards already carrying Flickity markup are skipped.
    const saved = document.createElement('template');
    saved.innerHTML = snap.html;
    [...saved.content.children].forEach((cell) => {
      if (!cell.dataset.handle || cell.classList.contains('ns-card-pending') || cell.querySelector('.flickity-viewport')) return;
      window.NostoSearch.cacheCard(cell.dataset.handle, cell.innerHTML.trim());
    });
    grid.innerHTML = snap.html;
    grid.classList.add('ns-loaded');
    if (snap.fallback) grid.classList.add('ns-fallback');
    loadedCount = snap.loadedCount || productCells().length;
    currentPage = snap.currentPage || getPageFromUrl();
    totalProducts = snap.totalProducts || loadedCount;
    updateCounts(totalProducts);
    updateLoadMore(loadedCount, totalProducts);
    const container = grid.closest('[data-products-grid]');
    if (container) container.classList.remove('ns-loading');
    window.scrollTo(0, snap.scrollY || 0);
    // Re-assert after image layout settles (heights are known from the cards'
    // fixed aspect ratio, but late styles can still nudge the document).
    requestAnimationFrame(() => window.scrollTo(0, snap.scrollY || 0));
    return true;
  };

  // Extra products so the desktop grid's last row stays full once the breakers' cells are placed
  // (breakers occupy col×row cells; pad up to a multiple of the 4 desktop columns).
  const breakerFill = () => {
    const tpl = document.getElementById('tpl-nosto-breakers');
    if (!tpl) return 0;
    let cells = 0;
    tpl.content.querySelectorAll('[data-breaker-type="desktop"]').forEach((b) => {
      cells += (parseInt(b.dataset.gridCols, 10) || 1) * (parseInt(b.dataset.gridRows, 10) || 1);
    });
    const mod = cells % 4;
    return mod ? 4 - mod : 0;
  };

  // Re-inject breakers from the hidden template into the grid
  const injectBreakers = (grid) => {
    const tpl = document.getElementById('tpl-nosto-breakers');
    if (!tpl) return;
    const breakers = tpl.content.querySelectorAll('[data-breaker-position]');
    breakers.forEach(b => {
      const pos = parseInt(b.dataset.breakerPosition, 10);
      const clone = b.cloneNode(true);
      const items = grid.querySelectorAll(':scope > .grid-item.product-item:not(.product-item--breaker):not(.breaker_item)');
      if (pos > 0 && pos <= items.length) {
        grid.insertBefore(clone, items[pos - 1]);
      } else {
        grid.appendChild(clone);
      }
    });
  };

  // Update result count display
  const updateCounts = (total) => {
    const el = document.querySelector('.collection__product-count');
    if (el) {
      el.textContent = total + (total === 1
        ? ' ' + (window.NS_PRODUCT_LABEL || 'product')
        : ' ' + (window.NS_PRODUCTS_LABEL || 'products'));
      el.classList.add('ns-count-ready');
    }

    // Update "See N products" CTA
    const cta = document.querySelector('[data-ns-see-count]');
    if (cta) {
      const span = cta.querySelector('span');
      if (span) {
        const tpl = total === 1 ? (window.NS_VIEW_ITEM || '') : (window.NS_VIEW_ITEMS || '');
        span.textContent = tpl.replace('__COUNT__', total);
      }
    }
  };

  // Update the "Showing X of Y" status and load-more button
  const updateLoadMore = (showing, total) => {
    const currentEl = document.querySelector('.current-products');
    const totalEl = document.querySelector('.total-products');
    if (currentEl) currentEl.textContent = showing;
    if (totalEl) totalEl.textContent = total;

    const btn = document.querySelector('.js-load-more');
    if (btn) {
      if (showing >= total) {
        btn.parentElement.style.display = 'none';
      } else {
        btn.parentElement.style.display = '';
        btn.removeAttribute('disabled');
        const loadMoreText = btn.querySelector('[load-more-text]');
        const loaderText = btn.querySelector('[loader-text]');
        if (loadMoreText) loadMoreText.classList.remove('hide');
        if (loaderText) loaderText.classList.add('hide');
      }
    }
  };

  // Run initial Nosto category search — clears grid, renders first page.
  // restorePages: on a non-cached Back, load all products up to the URL page. A reload / first load
  // passes false and loads page 1. anchor: scroll to it after build (Back only).
  const runSearch = (restorePages, anchor) => {
    const grid = document.querySelector('[data-nosto-grid]');
    if (!grid) return;

    const categoryId = grid.dataset.nostoCategoryId;
    const categoryPath = grid.dataset.nostoCategoryPath;
    if (!categoryId && !categoryPath) return;

    const container = grid.closest('[data-products-grid]');
    if (container) container.classList.add('ns-loading');

    const token = ++searchToken;

    // Load one Nosto page (PAGE_SIZE), or all pages up to the URL page on a Back-restore — plus a few
    // extra to keep the last row full under the breakers. Uses Nosto's PAGE_SIZE, not the theme grid.
    const initialPage = restorePages ? getPageFromUrl() : 1;

    const opts = { from: 0, size: initialPage * PAGE_SIZE + breakerFill() };
    if (window.NostoFacets) {
      const filters = window.NostoFacets.getFilters();
      const sort = window.NostoFacets.getSort();
      if (filters.length) opts.filter = window.NostoFacets.expandFilters(filters);
      if (sort) opts.sort = sort;
    }

    const args = {};
    if (categoryId) args.categoryId = categoryId;
    if (categoryPath) args.categoryPath = categoryPath;

    window.NostoSearch.categorySearch(args, opts)
      .then(res => {
        if (token !== searchToken) return;

        const products = res.products || {};
        const hits = products.hits || [];
        totalProducts = products.total || 0;

        updateCounts(totalProducts);

        if (facetsRoot && window.NostoFacets) {
          window.NostoFacets.render(products.facets || [], facetsRoot);
        }

        if (!hits.length) {
          // Never wipe a grid the fast path already populated — an empty
          // personalized response shouldn't erase real products on screen.
          if (grid.classList.contains('ns-fallback')) {
            if (container) container.classList.remove('ns-loading');
            return;
          }
          grid.innerHTML = '<div class="no-results"><p><strong>' +
            (window.NS_NO_RESULTS || 'No products found') + '</strong></p></div>';
          loadedCount = 0;
          updateLoadMore(0, 0);
          if (container) container.classList.remove('ns-loading');
          return;
        }

        // If the fast path already rendered these exact products in this
        // exact order, keep the DOM untouched — just adopt the state so
        // paging/tracking continue through the full Nosto flow.
        const sameAsDirect =
          grid.classList.contains('ns-fallback') &&
          Array.isArray(directIds) &&
          hits.length === directIds.length &&
          hits.every((h, i) => String(h.productId) === directIds[i]);
        if (sameAsDirect) {
          grid.classList.remove('ns-fallback');
          loadedCount = hits.length;
          currentPage = initialPage;
          setPage(currentPage);
          updateLoadMore(loadedCount, totalProducts);
          if (container) container.classList.remove('ns-loading');
          return;
        }

        // A swap must not displace products under a visitor who has scrolled
        // (Back-restore, or a late Nosto init after the fast path rendered).
        // The stored anchor is stale if they scrolled since the restore
        // render, so re-anchor to the product they are looking at right now
        // (same capture saveAnchor uses). If the personalized window doesn't
        // contain that product at all, skip the swap and stay on the direct
        // grid — the same call loadMore makes for a late init; direct-mode
        // paging carries on from here. Filter/sort changes are a deliberate
        // rebuild and are exempt (same condition runDirectSearch uses).
        let swapAnchor = anchor;
        const filtered = window.NostoFacets &&
          (window.NostoFacets.getFilters().length || window.NostoFacets.getSort());
        if (!filtered && grid.classList.contains('ns-fallback') && window.scrollY > 0) {
          const liveCell = findTopCell();
          if (liveCell && liveCell.dataset.handle) {
            swapAnchor = {
              handle: liveCell.dataset.handle,
              offset: Math.round(liveCell.getBoundingClientRect().top)
            };
          }
          if (swapAnchor &&
              !hits.some((h) => window.NostoSearch.getHandleFromUrl(h.url) === swapAnchor.handle)) {
            searchToken++;
            if (container) container.classList.remove('ns-loading');
            return;
          }
        }

        // Quick-add submits are handled by the delegated listener in nosto-search.js
        // (cells carry data-nosto-card-section), so no per-form binding is needed.
        const built = buildCards(hits);
        grid.classList.remove('ns-fallback');
        grid.innerHTML = '';
        const frag = document.createDocumentFragment();
        built.cells.forEach(el => frag.appendChild(el));
        grid.appendChild(frag);
        grid.classList.add('ns-loaded');
        loadedCount = hits.length;
        // Track pages explicitly — deriving from loadedCount inflated the
        // count (loadedCount includes breakerFill extras, so 38 cards made
        // ceil(38/36) = page 2 on FIRST load, and every back-restore then
        // fetched one page more than the visitor ever saw).
        currentPage = initialPage;
        setPage(currentPage);
        injectBreakers(grid);
        updateLoadMore(loadedCount, totalProducts);
        return built.done.then(() => {
          if (token !== searchToken) return;
          if (swapAnchor) { applyAnchor(swapAnchor); setTimeout(() => applyAnchor(swapAnchor), 0); }
        });
      })
      .then(() => {
        if (token !== searchToken) return;
        if (container) container.classList.remove('ns-loading');
      })
      .catch(err => {
        if (token !== searchToken) return;
        console.error('[NostoCategory]', err);
        if (container) container.classList.remove('ns-loading');
      });
  };

  // Load more — appends next batch to grid
  const loadMore = () => {
    const grid = document.querySelector('[data-nosto-grid]');
    if (!grid || loadedCount >= totalProducts) return;

    const categoryId = grid.dataset.nostoCategoryId;
    const categoryPath = grid.dataset.nostoCategoryPath;

    const btn = document.querySelector('.js-load-more');
    if (btn) {
      btn.setAttribute('disabled', true);
      const loadMoreText = btn.querySelector('[load-more-text]');
      const loaderText = btn.querySelector('[loader-text]');
      if (loadMoreText) loadMoreText.classList.add('hide');
      if (loaderText) loaderText.classList.remove('hide');
    }

    const restoreBtn = () => {
      if (!btn) return;
      btn.removeAttribute('disabled');
      const loadMoreText = btn.querySelector('[load-more-text]');
      const loaderText = btn.querySelector('[loader-text]');
      if (loadMoreText) loadMoreText.classList.remove('hide');
      if (loaderText) loaderText.classList.add('hide');
    };

    // Direct mode (Nosto never took over): page through the direct endpoint.
    if (grid.classList.contains('ns-fallback')) {
      // The user is now paging the direct grid — a Nosto init this late must
      // not adopt/swap it (its page-1 response would reset loadedCount under
      // a multi-page DOM and duplicate products on the next page). Invalidate
      // the pending upgrade; this session stays on the direct endpoint.
      searchToken++;
      directSearch(categoryId, { from: loadedCount, size: PAGE_SIZE })
        .then((res) => {
          const hits = (res && res.hits) || [];
          if (!hits.length) {
            updateLoadMore(loadedCount, loadedCount);
            return;
          }
          const built = buildCards(hits);
          const frag = document.createDocumentFragment();
          built.cells.forEach((el) => frag.appendChild(el));
          grid.appendChild(frag);
          loadedCount += hits.length;
          currentPage += 1;
          setPage(currentPage);
          updateLoadMore(loadedCount, totalProducts);
          return built.done;
        })
        .catch(restoreBtn);
      return;
    }

    const opts = { from: loadedCount };
    if (window.NostoFacets) {
      const filters = window.NostoFacets.getFilters();
      const sort = window.NostoFacets.getSort();
      if (filters.length) opts.filter = window.NostoFacets.expandFilters(filters);
      if (sort) opts.sort = sort;
    }

    const args = {};
    if (categoryId) args.categoryId = categoryId;
    if (categoryPath) args.categoryPath = categoryPath;

    window.NostoSearch.categorySearch(args, opts)
      .then(res => {
        const products = res.products || {};
        const hits = products.hits || [];
        if (!hits.length) {
          updateLoadMore(loadedCount, totalProducts);
          return;
        }

        const built = buildCards(hits);
        const frag = document.createDocumentFragment();
        built.cells.forEach(el => frag.appendChild(el));
        grid.appendChild(frag);
        loadedCount += hits.length;
        currentPage += 1;
        setPage(currentPage);
        updateLoadMore(loadedCount, totalProducts);
        return built.done;
      })
      .catch(err => {
        console.error('[NostoCategory]', err);
        restoreBtn();
      });
  };

  // ---------------------------------------------------------------------
  // FAST PATH — the grid's PRIMARY first render.
  //
  // Nosto's bootstrap (app embed -> market loader -> consent -> session) is
  // unreliable: identical loads initialize in 1.6s, 18.7s, or never (consent
  // unanswered, ad-blocked, or its intermittent stall). Waiting for it — even
  // with a rescue timer — meant seconds of skeletons on every unlucky load.
  //
  // Nosto's public search endpoint, by contrast, answers unauthenticated
  // plain-HTTP queries in ~200ms with the real category products. So the
  // grid renders from it IMMEDIATELY on every load (non-personalized order),
  // and the full Nosto flow upgrades the grid when — if — it initializes:
  // identical results keep the DOM untouched, different (personalized)
  // results swap in from cache. No timers, no waiting, no lottery.
  // ---------------------------------------------------------------------
  let directIds = null; // productIds of the current direct render, for the identical-result check

  // Account walking (suffixed market account first, bare fallback) lives in
  // NostoSearch.directSearchAny — shared with the SERP and autocomplete.
  const directSearch = (categoryId, opts) =>
    window.NostoSearch.directSearchAny({ categoryId: categoryId, size: opts.size, from: opts.from });

  const renderDirectCards = (hits, total, anchor) => {
    const gridNow = document.querySelector('[data-nosto-grid]');
    if (!hits.length || !gridNow || gridNow.classList.contains('ns-loaded')) return;
    directIds = hits.map((h) => String(h.productId));
    const built = window.NostoSearch.buildCards(hits, 'category');
    gridNow.innerHTML = '';
    const frag = document.createDocumentFragment();
    built.cells.forEach((el) => frag.appendChild(el));
    gridNow.appendChild(frag);
    gridNow.classList.add('ns-loaded', 'ns-fallback');
    injectBreakers(gridNow);
    loadedCount = hits.length;
    totalProducts = total || hits.length;
    if (total) updateCounts(total);
    updateLoadMore(loadedCount, totalProducts);
    const container = gridNow.closest('[data-products-grid]');
    if (container) container.classList.remove('ns-loading');
    if (anchor) {
      built.done.then(() => {
        applyAnchor(anchor);
        setTimeout(() => applyAnchor(anchor), 0);
      });
    }
  };

  // Last-resort source if even the direct endpoint is unreachable: the
  // Shopify collection itself. Only helps collections that have products in
  // Shopify — most main collections here are empty containers whose products
  // exist only in Nosto's category system.
  const productsJsonFallback = () => {
    const m = location.pathname.match(/\/collections\/[^/?#]+/);
    if (!m) return;
    fetch(m[0] + '/products.json?limit=' + PAGE_SIZE)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const products = (data && data.products) || [];
        const hits = products
          .filter((p) => !(p.tags && p.tags.indexOf('hide') !== -1))
          .map((p) => ({ url: '/products/' + p.handle, productId: String(p.id) }));
        renderDirectCards(hits, 0, null);
      })
      .catch(() => { /* keep skeletons; Nosto may still arrive */ });
  };

  const runDirectSearch = (restorePages, anchor) => {
    const grid = document.querySelector('[data-nosto-grid]');
    if (!grid || grid.classList.contains('ns-loaded')) return;
    // Filters/sort restored from the URL can't be sent to the direct query —
    // rendering without them would flash (or, if Nosto never initializes,
    // permanently show) the wrong products. Let the full Nosto flow own it.
    if (window.NostoFacets && (window.NostoFacets.getFilters().length || window.NostoFacets.getSort())) return;
    const categoryId = grid.dataset.nostoCategoryId;
    if (!categoryId || !window.NostoSearch.directSearchAny) {
      productsJsonFallback();
      return;
    }
    const initialPage = restorePages ? getPageFromUrl() : 1;
    const size = initialPage * PAGE_SIZE + breakerFill();

    // The inline preflight in theme.liquid fired this query while the HTML
    // was still parsing; use its (usually already-resolved) response for
    // page 1 instead of paying the round-trip again. It over-fetches (40) to
    // cover every breaker layout, so slice to this grid's exact size — the
    // sameAsDirect check needs the direct render and Nosto's own query
    // (which uses `size`) to be length-comparable.
    const normalize = (data) => {
      const p = data && data.data && data.data.search && data.data.search.products;
      return { hits: ((p && p.hits) || []).slice(0, size), total: (p && p.total) || 0 };
    };
    const request = (initialPage === 1 && window.__nsDirectPreflight)
      ? window.__nsDirectPreflight.then(normalize).then((res) =>
          // Empty preflight ≠ empty category (the head fetch can fail
          // transiently) — retry once from here before giving up.
          res.hits.length ? res : directSearch(categoryId, { size: size }))
      : directSearch(categoryId, { size: size });

    request
      .then((res) => {
        if (res && res.hits && res.hits.length) {
          currentPage = initialPage;
          renderDirectCards(res.hits, res.total, anchor);
        } else {
          productsJsonFallback();
        }
      })
      .catch(productsJsonFallback);
  };

  const init = () => {
    const grid = document.querySelector('[data-nosto-grid]');
    if (!grid) return;

    facetsRoot = document.querySelector('[data-nosto-facets]');
    if (facetsRoot && window.NostoFacets && !eventsBound) {
      window.NostoFacets.bindEvents(facetsRoot, () => runSearch(false));
      eventsBound = true;
    }

    // Restore filters/sort from the URL so shared & reloaded links show the same
    // results (must run before the first search so it includes those filters).
    if (window.NostoFacets && window.NostoFacets.readUrl) {
      window.NostoFacets.readUrl();
    }

    // Save the grid snapshot + top product on leave; visibilitychange covers
    // iOS/Safari where pagehide is unreliable.
    const saveState = () => { saveSnapshot(); saveAnchor(); };
    window.addEventListener('pagehide', saveState);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') saveState();
    });

    // Back restores the snapshot — the exact grid the visitor left, no
    // refetch, no re-render (mirrors the native collections' placeholder
    // restore in custom.js, which Nosto grids opt out of). The anchor
    // re-load below is the fallback when no snapshot survives; a reload
    // just loads page 1 top.
    let navType = '';
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      navType = nav ? nav.type : '';
    } catch (e) { /* ignore */ }
    const isBack = navType === 'back_forward';

    const snap = isBack ? readSnapshot() : null;
    if (snap && restoreSnapshot(snap)) {
      // The snapshot IS the state the visitor left — run no searches.
      try { history.scrollRestoration = 'manual'; } catch (e) { /* ignore */ }
    } else {
      const anchor = isBack ? readAnchor() : null;
      try { history.scrollRestoration = (anchor || navType === 'reload') ? 'manual' : 'auto'; } catch (e) { /* ignore */ }

      // Fast path renders the grid right away from the direct endpoint; the
      // full Nosto flow runs in parallel and upgrades it when it initializes.
      runDirectSearch(isBack, anchor);
      if (isBack) runSearch(true, anchor);
      else runSearch(false, null);
    }

    // Load more click delegation
    document.addEventListener('click', e => {
      const el = e.target.closest('.js-load-more');
      if (!el) return;
      e.preventDefault();
      loadMore();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
